import moment from "moment";
import {
  EnhancedRouteItem,
  ICargo,
  IOrder,
  IOrderProduct,
  IRouteItem,
  IRouteTiming,
  Timestamp,
} from "../types";

import { TranslateFn } from "../types/i18n";
import { OrderStatus } from "../types/order/IOrder";
import { byStopNumber } from "../utils/comparators";

export const getRouteItemId = (stop: EnhancedRouteItem): string =>
  stop.routeId || stop.location.id;

export const getPhysicalVisitKey = (stop: EnhancedRouteItem): string =>
  JSON.stringify({
    location: stop.location.id || {
      lat: stop.location.coordinate?.lat,
      lon: stop.location.coordinate?.lon,
    },
    isPickup: Boolean(stop.isPickup),
    timing: stop.timing || {},
  });

export const groupCompatibleRouteStops = (
  stops: EnhancedRouteItem[],
): EnhancedRouteItem[] => {
  const groups = new Map<string, EnhancedRouteItem[]>();
  stops.forEach((stop) => {
    const key = getPhysicalVisitKey(stop);
    groups.set(key, [...(groups.get(key) || []), stop]);
  });
  return Array.from(groups.values()).flat();
};

// ---------------------------------------------------------------------------
// Cargo → route-stop cargo (ICargo subset used by map/planner views)
// ---------------------------------------------------------------------------

/** Maps order cargo lines to the cargo shape carried on enhanced route stops. */
export const mapOrderCargoToRouteCargos = (
  cargo: IOrderProduct[] | undefined | null,
): ICargo[] | undefined =>
  cargo?.map((item) => ({
    id: item.id,
    quantity: item.quantity,
    description: item.descriptionShort,
    weight: item.weight,
    length: item.length,
    width: item.width,
    height: item.height,
    itinerary: {
      pickupStopNumber: item.itinerary?.pickupStopNumber,
      deliveryStopNumber: item.itinerary?.deliveryStopNumber,
    },
  }));

/** Maps driver-routes API cargo to the route-stop cargo shape. */
export const mapApiCargoToRouteCargo = (cargo: any): ICargo => ({
  id: cargo.id,
  quantity: cargo.quantity,
  description: cargo.description,
  weight: cargo.weight,
  length: cargo.length,
  width: cargo.width,
  height: cargo.height,
  itinerary: {
    pickupStopNumber: cargo.itinerary?.pickupStopNumber,
    deliveryStopNumber: cargo.itinerary?.deliveryStopNumber,
  },
});

// ---------------------------------------------------------------------------
// Driver-routes API item → EnhancedRouteItem
// ---------------------------------------------------------------------------

export interface MapDriverRouteApiItemOptions {
  /** Stop number to assign. Omit to use `item.routeStopNumber || 0`. */
  stopNumber?: number;
  isAvailable?: boolean;
  /** Include postCode/city on the location object (planner map needs them). */
  includeFullLocation?: boolean;
  /** Read timing from the API payload; otherwise use earliest/latest false. */
  useApiTiming?: boolean;
}

/**
 * Maps a single driver-routes REST API item to {@link EnhancedRouteItem}.
 * Shared by the planner and the driver-map routes tab.
 */
export const mapDriverRouteApiItem = (
  item: any,
  options: MapDriverRouteApiItemOptions = {},
): EnhancedRouteItem => {
  const {
    stopNumber = item.routeStopNumber || 0,
    isAvailable = false,
    includeFullLocation = false,
    useApiTiming = false,
  } = options;

  const location: EnhancedRouteItem["location"] = {
    id: item.id,
    displayName: item.displayName,
    addressLine: item.stop?.location?.addressLine,
    postCode: includeFullLocation ? item.stop?.location?.postCode : "",
    city: includeFullLocation ? item.stop?.location?.city : "",
    workspaceId: item.workspaceId,
    customerId: "",
    coordinate: item.stop?.location?.coordinate,
    createdAt: null,
    countryCode: "",
  };

  const timing: IRouteTiming = useApiTiming
    ? item.stop?.timing || { earliest: false, latest: false }
    : { earliest: false, latest: false };

  return {
    routeId: item.id,
    orderId: item.orderId,
    location,
    stopNumber,
    stopDate: Timestamp.fromMoment(moment(item.routeDate)),
    timing,
    requiredTimeWindows: item.stop?.requiredTimeWindows,
    preferredTimeWindows: item.stop?.preferredTimeWindows,
    status: item.status,
    isPickup: item.isPickup,
    isAvailable,
    stopDisplayName: item.stop?.location?.displayName,
    orderDisplayName: item.displayName,
    cargos: item.cargos?.map(mapApiCargoToRouteCargo),
  } as EnhancedRouteItem;
};

/** Statuses shown on the driver-map routes tab. */
export const DRIVER_ROUTE_MAP_STATUSES: OrderStatus[] = [
  OrderStatus.InProgress,
  OrderStatus.Completed,
  OrderStatus.Cancelled,
  OrderStatus.Scheduled,
];

/**
 * Maps a driver-routes API response for the map routes tab: filters to
 * in-flight statuses, maps each item, then assigns sequential display stop
 * numbers (1-based) for consistent UI labelling.
 */
export const mapDriverRoutesForMapView = (
  response: any[],
): EnhancedRouteItem[] => {
  const stops = response
    .filter((item: any) => DRIVER_ROUTE_MAP_STATUSES.includes(item.status))
    .map((item: any) =>
      mapDriverRouteApiItem(item, {
        isAvailable: false,
        includeFullLocation: false,
        useApiTiming: false,
      }),
    );

  return assignDisplayStopNumbers(stops);
};

/**
 * Re-numbers stops sequentially (1-based) for display contexts.
 * Sorts by the current `stopNumber` first (non-mutating).
 */
export const assignDisplayStopNumbers = <T extends { stopNumber: number }>(
  stops: T[],
): T[] => {
  const sorted = [...stops].sort(byStopNumber);
  return sorted.map((stop, index) => ({
    ...stop,
    stopNumber: index + 1,
  }));
};

// ---------------------------------------------------------------------------
// Order embedded route → EnhancedRouteItem (orders map tab)
// ---------------------------------------------------------------------------

/** Enriches a single order route stop for map/planner display. */
export const mapOrderRouteItemToEnhanced = (
  routeItem: IRouteItem,
  order: Pick<IOrder, "cargo">,
  orderDisplayName: string,
): EnhancedRouteItem => ({
  ...routeItem,
  isAvailable: false,
  orderDisplayName,
  stopDisplayName: routeItem.location.displayName,
  cargos: mapOrderCargoToRouteCargos(order.cargo),
});

/** Maps all stops on an order to enhanced route items for map views. */
export const mapOrderRouteToEnhanced = (
  order: Pick<IOrder, "route" | "cargo">,
  orderDisplayName: string,
): EnhancedRouteItem[] =>
  (order.route || []).map((item) =>
    mapOrderRouteItemToEnhanced(item, order, orderDisplayName),
  );

// ---------------------------------------------------------------------------
// Firestore routes collection → EnhancedRouteItem (useRoute hook)
// ---------------------------------------------------------------------------

/** Maps a Firestore `routes` document to {@link EnhancedRouteItem}. */
export const mapFirestoreRouteDoc = (
  docId: string,
  data: any,
): EnhancedRouteItem =>
  ({
    routeId: docId,
    orderId: data.orderId || "",
    location: {
      id: data.stop?.location?.id || docId,
      displayName: data.stop?.location?.displayName || data.displayName || "",
      addressLine: data.stop?.location?.addressLine || "",
      postCode: data.stop?.location?.postCode || "",
      city: data.stop?.location?.city || "",
      workspaceId: data.workspaceId || "",
      customerId: data.stop?.location?.customerId || "",
      coordinate: data.stop?.location?.coordinate || { lat: 0, lon: 0 },
      createdAt: data.stop?.location?.createdAt || data.createdAt,
      countryCode: data.stop?.location?.countryCode || "",
      description: data.stop?.location?.description || "",
      imgUrl: data.stop?.location?.imgUrl || "",
    },
    stopNumber: data.routeStopNumber || 0,
    stopDate: data.routeDate || data.stop?.stopDate,
    timing: {
      earliest: data.stop?.timing?.earliest || false,
      latest: data.stop?.timing?.latest || false,
      before: data.stop?.timing?.before,
      after: data.stop?.timing?.after,
    },
    requiredTimeWindows: data.stop?.requiredTimeWindows,
    preferredTimeWindows: data.stop?.preferredTimeWindows,
    status: (data.status as OrderStatus) || OrderStatus.New,
    isPickup: data.isPickup || false,
    isAvailable: false,
    stopDisplayName: data.stop?.location?.displayName || data.displayName || "",
    orderDisplayName: data.displayName || "",
    deliveryComment: data.stop?.deliveryComment,
    signatureUrl: data.stop?.signatureUrl,
    signedBy: data.stop?.signedBy,
    cargos:
      data.cargos?.map((cargo: any) => ({
        id: cargo.id || "",
        quantity: cargo.quantity || 1,
        description: cargo.description || cargo.descriptionShort || "",
        weight: cargo.weight,
        length: cargo.length,
        width: cargo.width,
        height: cargo.height,
        itinerary: {
          pickupStopNumber: cargo.itinerary?.pickupStopNumber,
          deliveryStopNumber: cargo.itinerary?.deliveryStopNumber,
        },
      })) || [],
  }) as EnhancedRouteItem;

// ---------------------------------------------------------------------------
// Enhanced route → RouteTimeline stop shape (OrderDetails)
// ---------------------------------------------------------------------------

/** Display-only stop shape consumed by {@link RouteTimeline}. */
export interface TimelineStopViewModel {
  stopNumber: number;
  isPickup?: boolean;
  location: {
    addressLine?: string | null;
    displayName?: string | null;
    city?: string | null;
    postCode?: string | null;
  };
  stopDate?: Timestamp;
  status?: string;
  completedAt?: Timestamp | null;
  signatureUrl?: string | null;
  timing?: IRouteTiming;
}

export const mapEnhancedRouteToTimelineStop = (
  route: EnhancedRouteItem,
): TimelineStopViewModel => ({
  stopNumber: route.stopNumber,
  isPickup: route.isPickup,
  location: {
    addressLine: route.location.addressLine,
    displayName: route.location.displayName,
    city: route.location.city,
    postCode: route.location.postCode,
  },
  stopDate: route.stopDate,
  status: route.status,
  completedAt: route.status === "Completed" ? route.stopDate : null,
  signatureUrl: route.signatureUrl,
  timing: route.timing,
});

// ---------------------------------------------------------------------------
// Route stop timing display labels
// ---------------------------------------------------------------------------

/** Context-specific i18n keys and formatting rules for stop timing labels. */
export type StopTimingLabelVariant =
  "timeline" | "planner" | "create-order" | "orders-map" | "orders-preview";

export interface FormatStopTimingLabelOptions {
  variant: StopTimingLabelVariant;
  /** Used by the timeline variant to prefix the label with the stop date. */
  stopDate?: Timestamp | null;
  /** Returned when timing is absent or no rule matches (default ""). */
  fallback?: string;
}

/**
 * Canonical stop timing label formatter. Each variant preserves the exact
 * output format and i18n keys of its original call site.
 */
export const formatStopTimingLabel = (
  timing: Partial<IRouteTiming> | null | undefined,
  t: TranslateFn,
  options: FormatStopTimingLabelOptions,
): string | null => {
  const { variant, stopDate, fallback = "" } = options;

  if (!timing) {
    return variant === "timeline" ? null : fallback;
  }

  const { earliest, latest, before, after } = timing;

  switch (variant) {
    case "timeline": {
      const datePrefix = stopDate
        ? `${Timestamp.toDisplayDate(stopDate)} · `
        : "";

      if (earliest) {
        return `${datePrefix}${t("common:asap", "Så snart som mulig")}`;
      }
      if (latest) {
        return `${datePrefix}${t("common:as_late_as_possible", "Så sent som mulig")}`;
      }
      if (after || before) {
        const window =
          after && before
            ? `${after}-${before}`
            : after
              ? `${t("common:after", "Etter")} ${after}`
              : before
                ? `${t("common:before", "Før")} ${before}`
                : "";
        return `${datePrefix}${window}`;
      }
      if (stopDate) {
        return Timestamp.toDisplayDate(stopDate);
      }
      return null;
    }

    case "planner": {
      if (earliest) return t("planner:route_planner.timing.earliest");
      if (latest) return t("planner:route_planner.timing.latest");
      if (before && after) {
        return t("planner:route_planner.timing.between", {
          startTime: after,
          endTime: before,
        });
      }
      if (before) {
        return t("planner:route_planner.timing.before", { time: before });
      }
      if (after) {
        return t("planner:route_planner.timing.after", { time: after });
      }
      return fallback;
    }

    case "create-order": {
      // Preserves RouteSelection branch order (before/after checked before between).
      if (earliest) return t("create.route_selection.earliest_possible");
      if (latest) return t("create.route_selection.latest_possible");
      if (before) return `${t("create.route_selection.before")} ${before}`;
      if (after) return `${t("create.route_selection.after")} ${after}`;
      if (before && after) {
        return `${t("create.route_selection.between")} ${after} - ${before}`;
      }
      return fallback;
    }

    case "orders-map": {
      if (earliest) return t("orders:create.route_selection.earliest_possible");
      if (latest) return t("orders:create.route_selection.latest_possible");
      if (before && after) {
        return `${t("orders:create.route_selection.between")} ${after} - ${before}`;
      }
      if (before) {
        return `${t("orders:create.route_selection.before")} ${before}`;
      }
      if (after) {
        return `${t("orders:create.route_selection.after")} ${after}`;
      }
      return fallback;
    }

    case "orders-preview": {
      if (earliest) return t("orders:create.route_selection.earliest");
      if (latest) return t("orders:create.route_selection.latest");
      if (before && after) {
        return `${t("orders:create.route_selection.between")} ${after} - ${before}`;
      }
      if (before) {
        return `${t("orders:create.route_selection.before")} ${before}`;
      }
      if (after) {
        return `${t("orders:create.route_selection.after")} ${after}`;
      }
      return fallback;
    }

    default:
      return fallback;
  }
};

/**
 * Timing label for {@link RouteTimeline} (date prefix + common: namespace).
 * Thin wrapper around {@link formatStopTimingLabel}.
 */
export const formatRouteTimelineTiming = (
  stop: {
    stopDate?: Timestamp | null;
    timing?: Partial<IRouteTiming> | null;
  },
  t: TranslateFn,
): string | null =>
  formatStopTimingLabel(stop.timing, t, {
    variant: "timeline",
    stopDate: stop.stopDate,
  });
